import { Component, Host, h, Prop, Element, Listen, Method, Event, EventEmitter, State, Watch } from '@stencil/core';
import { Attributes, hasSlot, inheritAriaAttributes, observeAriaAttributes } from '../../utils';
import { MenuItem } from '../menu-item/menu-item.interface';
import { getFormFieldId } from '../../utils/form.util';

@Component({
  tag: 'o2-search',
  styleUrl: 'search.scss',
  scoped: true,
})
export class O2Search {
  private delayTimer = null;
  private hasAffixSlot: boolean;

  private searchId = `o2-search-${searchIds++}`;

  /**
   * Reference to the input element.
   */
  private input: HTMLO2InputElement;

  /**
   * Initial data from setData method
   */
  private data: Array<MenuItem> = [];

  /**
   * Instance of MutationObserver
   */
  private mutationObserver: MutationObserver;

  @Element() private el!: HTMLO2SearchElement;

  /**
   * Inherited attributes from the host element.
   */
  @State() inheritedAttributes: Attributes = {};

  /**
   * State of the clear button.
   */
  @State() isClearButtonVisible: boolean = false;

  /**
   * Filtered data in the list based on the input value.
   */
  @State() filteredData: Array<MenuItem> = [];

  /**
   * Indicates whether the input allows propagation of native events.
   */
  @Prop() native?: boolean = false;

  /**
   * Turns on/off the autocomplete mode of search
   */
  @Prop() autocomplete?: boolean = false;

  /**
   * Toggles the filtering mode for search autocomplete. Default is true. Turn it off in case of filtering on your own
   */
  @Prop() filter?: boolean = true;

  /**
   * If set, label will show on top of search component
   */
  @Prop() label?: string;

  /**
   * Set the search placeholder
   */
  @Prop() placeholder?: string;

  /**
   * Set the search value
   */
  @Prop() value?: string;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name?: string;

  /**
   * If `true`, the user cannot interact with the input.
   */
  @Prop({
    reflect: true,
  })
  disabled? = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * If `true`, the requirement text is shown. It depends on required property. If required is true, requirement text will be "(povinné)", otherwise it displays "(nepovinné)"
   */
  @Prop() showRequirementText? = false;

  /**
   * If `true`, the user cannot modify the value.
   */
  @Prop({
    reflect: true,
  })
  readonly? = false;

  /**
   * Set number of maximum items that will be shown with scroll bar.
   */
  @Prop({
    reflect: true,
  })
  maxVisibleItems? = 7;

  /**
   * Emitted when keyboard input on keyup with delay
   */
  @Event() o2Search!: EventEmitter<InputEvent>;

  /**
   * Emitted on clearButton click.
   */
  @Event() o2Clear!: EventEmitter<any>;

  /**
   * Emitted when o2-menu-item inside search is clicked
   */
  @Event() o2ItemClick!: EventEmitter<any>;

  /**
   * Emitted when the input is ready.
   */
  @Event() o2Ready!: EventEmitter<Event>;

  /**
   * Method to set data.
   */
  @Method()
  async setData(data: Array<MenuItem>) {
    if (this.autocomplete) {
      this.data = data;

      let value = this.el.querySelector('o2-input').value;
      if (value !== undefined && value !== '') {
        this.filterData(value);
      }
    } else {
      console.error('[o2-search]: Please set autocomplete in order to use setData');
    }
  }

  /**
   * Method to get selected value.
   */
  @Method()
  async getValue() {
    return this.input.value;
  }

  /**
   * Search input value in the list
   */
  @Listen('o2Input')
  protected handleInput(event: InputEvent) {
    this.searchHandler(event);
  }

  /**
   * Clear filteredData if the clicked element is outside the  `o2-input` component.
   */
  @Listen('click', { target: 'window' })
  handleWindowClick(e: Event) {
    const targetElement = e.target as HTMLElement;
    if (!this.el.contains(targetElement)) {
      this.filteredData = [];
    }
  }

  /**
   * Listens to keyboard arrows and enter key to navigate through the list.
   */
  @Listen('keydown')
  protected handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const activeItem: HTMLO2MenuItemElement = this.el.querySelector('o2-menu-item[active]');
      if (activeItem) {
        this.input.value = activeItem.text;
        this.filteredData = [];
      }

      if (this.autocomplete) {
        this.filteredData = [];
      }
    }
  }

  /**
   * Watch filteredData and clear that array if there is only one item and it is the same as the input value. Empty array is needed for the list to close.
   */
  @Watch('filteredData')
  watchFilteredDataHandler(newValue: Array<MenuItem>) {
    if (newValue.length === 1 && newValue[0].text === this.input.value) {
      this.filteredData = [];
    }

    if (newValue.length > 0 && this.input.value == '') {
      this.filteredData = [];
    }
  }

  /**
   * Update the input when the error changes
   */
  @Watch('error')
  protected errorChanged() {
    const input = this.el.querySelector('o2-input') as HTMLO2InputElement;
    input.toggleAttribute('error');
  }

  private searchHandler = event => {
    const value = (event.target as HTMLInputElement).value;

    this.isClearButtonVisible = value.length > 0;

    clearTimeout(this.delayTimer);
    this.delayTimer = setTimeout(() => {
      if (this.autocomplete) {
        this.filterData(value);
      }

      this.o2Search.emit(event.target.value);
    }, 350);
  };

  /**
   * Method to normalize input string without special characters.
   * @param str
   * @returns string
   */
  private normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  /**
   * Method to filter data based on the input value and set filtered data.
   * @param searchValue
   */
  private filterData = (searchValue: string) => {
    const value = searchValue.toLowerCase();

    requestAnimationFrame(() => {
      if (this.filter) {
        const normalizedValue = this.normalizeString(value);
        const filter = this.data.filter((item: MenuItem) => this.normalizeString(item.text).includes(normalizedValue));

        if (!this.isArrayEqual(this.filteredData, filter)) {
          this.filteredData = filter;
        }
      } else {
        if (!this.isArrayEqual(this.filteredData, this.data)) {
          this.filteredData = this.data;
        }
      }
    });
  };

  private onCLickClearInputHandler = () => {
    this.input.value = '';
    this.isClearButtonVisible = false;
    this.input.setFocus();

    if (this.autocomplete) {
      this.filteredData = [];
    }

    this.o2Clear.emit();
  };

  private onClickMenuItemHandler = (event: Event) => {
    const htmlMenuItem: HTMLO2MenuItemElement = event.target as HTMLO2MenuItemElement;

    this.input.value = htmlMenuItem.text;
    this.filteredData = [];

    this.o2ItemClick.emit({
      text: htmlMenuItem.text,
      value: htmlMenuItem.value,
    });
  };

  private isArrayEqual = (arr1: Array<MenuItem>, arr2: Array<MenuItem>) => {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  };

  private setMenuListMaxHeight(): { [key: string]: string } {
    return {
      'max-height': `${this.maxVisibleItems * 36}px`,
    };
  }

  connectedCallback() {
    this.hasAffixSlot = hasSlot(this.el, '[slot="affix"]');

    this.mutationObserver = observeAriaAttributes(this.el as HTMLElement, () => {
      // merge attributes
      const newAttributes = inheritAriaAttributes(this.el as HTMLElement);
      this.inheritedAttributes = { ...this.inheritedAttributes, ...newAttributes };
    });
  }

  componentWillLoad() {
    this.inheritedAttributes = inheritAriaAttributes(this.el as HTMLElement);
  }

  componentDidRender() {
    this.o2Ready.emit();
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect();
  }

  render() {
    const isFilteredDataEmpty = !(this.filteredData.length > 0);

    return (
      <Host role="search">
        <div class={`o2-search ${this.readonly ? 'o2-search--readonly' : ''} ${!this.isClearButtonVisible && 'o2-search--hidden-suffix'}`}>
          <o2-input
            id={this.searchId}
            ref={el => (this.input = el)}
            label={this.label}
            placeholder={this.placeholder}
            value={this.value}
            error={this.error}
            name={this.name}
            disabled={this.disabled}
            required={this.required}
            readonly={this.readonly}
            showRequirementText={this.showRequirementText}
            leading-icon="search"
            native={this.native}
            data-form-field-id={getFormFieldId(this.el) && getFormFieldId(this.el)}
            role="combobox"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded={this.filteredData.length > 0 ? 'true' : 'false'}
            aria-controls={`${this.searchId}-list`}
            {...this.inheritedAttributes}
          >
            <div slot="clear-icon">
              <button type="button" aria-label="Vyprázdniť pole" onClick={this.onCLickClearInputHandler}>
                <o2-icon name="close" color="blue-action" size={20}></o2-icon>
              </button>
            </div>
          </o2-input>

          {/* TODO: move into input and use suffixSlot  */}
          {this.hasAffixSlot && (
            <div class={`search__affix ${this.disabled ? 'search__affix--disabled' : ''} ${this.autocomplete && this.isClearButtonVisible ? 'search__affix--hidden' : ''}`}>
              <slot name="affix"></slot>
            </div>
          )}

          {this.autocomplete && !isFilteredDataEmpty && (
            <o2-menu id={`${this.searchId}-list`} role="listbox" for={this.searchId} open={!isFilteredDataEmpty} style={this.setMenuListMaxHeight()}>
              {this.filteredData.map((item: MenuItem, i: number) => {
                return (
                  <o2-menu-item
                    id={`${this.searchId}-list-item-${i}`}
                    role="option"
                    value={item.value}
                    text={item.text}
                    selected={item.selected}
                    disabled={item.disabled}
                    onClick={this.onClickMenuItemHandler}
                  >
                    {item.html ? <div innerHTML={item.html}></div> : <span slot="text">{item.text}</span>}
                  </o2-menu-item>
                );
              })}
            </o2-menu>
          )}
        </div>
      </Host>
    );
  }
}

let searchIds = 0;
